import {
  faBell,
  faComments,
  faMessage,
  faPhone,
  faReply,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo } from 'react';

import { Channel } from '@/pages/Timeline/components/MessageEvent/types';
import { Message, MessageAttachment } from '@/types/message';
import DualTime from '@/ui/components/DualTime';
import Tooltip from '@/ui/components/Tooltip';

import { AttachmentCard } from './EmailMessage';
import MessageErrorIndicator from './MessageErrorIndicator';
import { MessageBubbleProps } from './types';

const MessageBubble = ({
  message,
  isPending,
  timezone,
  onReply,
  onDownload
}: MessageBubbleProps) => {
  const msg = message as Message;
  const isInbound = !isPending && msg.direction === 'in';

  let handleDownload: ((attachmentId: string) => Promise<{ url: string }>) | undefined;
  if (!isPending && onDownload) {
    handleDownload = (attachmentId: string) => onDownload(attachmentId, msg.kustomer_id);
  }

  const icon = useMemo(() => {
    switch (message.channel) {
      case Channel.SMS:
        return faMessage;
      case Channel.CHAT:
        return faComments;
      case Channel.PUSH:
        return faBell;
      case Channel.VOICE:
        return faPhone;
      default:
        return faMessage;
    }
  }, [message.channel]);

  const iconColor = useMemo(() => {
    switch (message.channel) {
      case Channel.SMS:
        return 'bg-blue-500';
      case Channel.CHAT:
        return 'bg-purple-500';
      case Channel.PUSH:
        return 'bg-yellow-500';
      case Channel.VOICE:
        return 'bg-green-500';
      default:
        return 'bg-blue-500';
    }
  }, [message.channel]);

  return (
    <div
      className={`group flex items-end gap-2 w-full mb-4 ${isInbound ? 'flex-row' : 'flex-row-reverse'}`}>
      <div className="relative flex-shrink-0">
        <div
          className={`inline-flex h-8 w-8 items-center justify-center rounded-full ${iconColor} ${isPending ? 'opacity-60' : ''}`}>
          <FontAwesomeIcon className="text-white text-xs" icon={icon} />
        </div>
        {!isPending && msg.error_message && (
          <MessageErrorIndicator errorMessage={msg.error_message} />
        )}
      </div>

      <div className={`flex min-w-0 flex-col max-w-sm ${isInbound ? 'items-start' : 'items-end'}`}>
        <div
          className={`min-w-0 px-4 py-2.5 rounded-2xl shadow-sm ${
            isInbound
              ? 'rounded-bl-sm bg-gray-100 text-gray-900'
              : 'rounded-br-sm bg-blue-600 text-white'
          } ${isPending ? 'opacity-60' : ''}`}>
          <p className="text-sm leading-relaxed break-words">{message.preview}</p>
          {!isPending && msg.attachments && msg.attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {msg.attachments.map((a: MessageAttachment) => (
                <AttachmentCard key={a.id} a={a} onDownload={handleDownload} />
              ))}
            </div>
          )}
        </div>
        <span className="mt-1 text-xs text-gray-400 leading-none">
          {isPending ? (
            <span className="flex items-center gap-1">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
              <span>Sending...</span>
            </span>
          ) : (
            <>
              {msg.sent_at && <DualTime dateTime={new Date(msg.sent_at)} memberTZ={timezone} />}
              {msg.created_by?.first_name && msg.created_by?.last_name && (
                <>
                  <span className="mx-1">·</span>
                  <span>
                    {msg.created_by.first_name} {msg.created_by.last_name}
                  </span>
                </>
              )}
            </>
          )}
        </span>
      </div>

      {!isPending && onReply && !msg.error_message && (
        <Tooltip tooltipContent={'Reply'}>
          <button
            onClick={() => onReply(msg)}
            aria-label="Reply to this message"
            className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            type="button">
            <FontAwesomeIcon icon={faReply} className="text-xs" />
          </button>
        </Tooltip>
      )}
    </div>
  );
};

export default MessageBubble;
